import { CircularProgress, Stack, Typography } from '@mui/material'
import jschardet from 'jschardet'
import { type FC, type ReactElement, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'

import { useAppSelector } from '../../../../../shared/hooks'
import { Button } from '../../../../../shared/ui'
import { BrowseIcon, FileIcon } from '../../icons'
import type {
	TExportContentProps,
	TExportedProfile,
	TExportState
} from '../../types.ts'
import ExportContent from '../ExportContent/ExportContent.tsx'
import { ExportCsvExample } from '../ExportCsvExample'
import ExportStep from '../ExportStep/ExportStep.tsx'

const ExportFromCsv: FC<TExportContentProps> = ({
	contactsCount,
	option,
	setContactsCount,
	setDataToExport,
	setIsFileUploaded
}: TExportContentProps): ReactElement => {
	const { appExportConnector } = useAppSelector(state => state.app)
	const profilesExport = appExportConnector?.profilesExport
	const [error, setError] = useState('')
	const [isLoading, setIsLoading] = useState(false)

	const { acceptedFiles, getInputProps, getRootProps, isDragActive, open } =
		useDropzone({
			maxFiles: 1,
			multiple: false,
			noClick: true,
			noKeyboard: true
		})

	const handleFileFullRead = async (content: ArrayBuffer | null | string) => {
		try {
			const response =
				await profilesExport?.extractFullLinkedInContacts(content)
			const list = Array.isArray(response)
				? (response as TExportedProfile[])
				: []

			if (list.length) {
				setError('')
				setDataToExport(list)
				setContactsCount(list.length)
				setIsFileUploaded(true)
			} else {
				setError('There are no LinkedIn profiles. Please, try another file.')
			}
		} catch (error) {
			setError(error instanceof Error ? error.message : String(error))
		} finally {
			setIsLoading(false)
		}
	}

	const handleReaderError = () => {
		setIsLoading(false)
		setError('There was a problem uploading your file. Please try again.')
	}

	const readFileContent = async (
		targetFile: File[],
		onRead: (content: ArrayBuffer | null | string) => Promise<void>
	) => {
		if (targetFile.length && profilesExport) {
			const getExportState = (await profilesExport.getState()) as
				| TExportState
				| null
				| undefined
			if (getExportState) {
				if (
					getExportState.phase === 'completed' ||
					getExportState.phase === 'failed'
				) {
					await profilesExport.clear()
				} else {
					return setError('Finish the started export')
				}
			}
			setIsLoading(true)

			const file = structuredClone(targetFile)
			const fileEncodingReader = new FileReader()
			fileEncodingReader.readAsArrayBuffer(file[0])
			await new Promise<void>((resolve, reject) => {
				fileEncodingReader.onload = () => resolve()
				fileEncodingReader.onerror = reject
			})

			const buffer = fileEncodingReader.result
			if (buffer == null || !(buffer instanceof ArrayBuffer)) {
				handleReaderError()
				return
			}
			const raw = new TextDecoder('iso-8859-1').decode(new Uint8Array(buffer))
			const detected = jschardet.detect(raw)
			const encoding = (detected?.encoding ?? 'utf-8') as string

			const fileReader = new FileReader()
			fileReader.onload = () =>
				onRead(fileReader.result as string | ArrayBuffer | null)
			fileReader.onerror = handleReaderError
			fileReader.readAsText(targetFile[0], encoding)
		}
	}

	useEffect(() => {
		;(async () => {
			await readFileContent(acceptedFiles, handleFileFullRead)
		})()
	}, [acceptedFiles])

	const { title = '' } = option

	if (isLoading) {
		return <CircularProgress size='56px' sx={{ alignSelf: 'center' }} />
	}

	return (
		<ExportStep title={title}>
			<ExportContent
				contactsCount={contactsCount}
				infoElement={<ExportCsvExample />}
				option={option}
			>
				<Stack {...getRootProps()} sx={styles.wrapper(isDragActive)}>
					<input {...getInputProps()} />
					<FileIcon sx={styles.icon} />
					<Typography sx={styles.title}>
						Drop your file here or select a file
					</Typography>
					<Button
						className='exportFromCsv-button'
						onClick={open}
						variant='primary'
					>
						<BrowseIcon sx={{ fontSize: '18px' }} />
						Browse files
					</Button>
				</Stack>
				{error && <Typography sx={styles.error}>{error}</Typography>}
			</ExportContent>
		</ExportStep>
	)
}

const styles = {
	error: {
		color: 'var(--red-indicator)',
		fontSize: '12px',
		fontWeight: 600,
		marginTop: '16px'
	},
	icon: { fontSize: '54px' },
	title: {
		fontSize: '14px',
		fontWeight: 600,
		marginTop: '4px'
	},
	wrapper: (isDragActive: boolean) => ({
		'& > .exportFromCsv-button': {
			height: '32px',
			marginTop: '16px'
		},
		alignItems: 'center',
		backgroundColor: isDragActive
			? 'rgb(87 160 34 / 20%)'
			: 'var(--background2)',
		backgroundImage:
			"url(\"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%23B3B6BFFF' stroke-width='2' stroke-dasharray='4%2c 6' stroke-dashoffset='0' stroke-linecap='round'/%3e%3c/svg%3e\")",
		borderRadius: '8px',
		color: isDragActive ? '#04c300' : 'var(--light-grey)',
		padding: '32px',
		transition: 'color 0.2s ease-in-out, background-color 0.2s ease-in-out'
	})
}

export default ExportFromCsv
